import { validateNode } from '../../../core/policies/node-validation/validate-node';
import {
	getWorkspaceEditLock,
	type WorkspaceEditLockMap,
} from '../../../core/policies/workspace/edit-lock';
import {
	type OutlineRowGroup,
} from '../../../core/policies/workspace/derive-outline-rows';
import type { JourneyNode, NodeType } from '../../../core/entities/node';
import { formatNodeTimestamp } from '../../preferences/i18n';
import { useLocale } from '../../preferences/i18n/LocaleProvider';

export function OutlineWorkspaceTable({
	activeEditLocks,
	collapsedGroupIds,
	focusedNodeId,
	groups,
	inlineDraft,
	inlineEditingNodeId,
	locale,
	onCancelInlineEdit,
	onEdit,
	onInlineDraftChange,
	onLocate,
	onOpenFullEditor,
	onRequestDelete,
	onSaveInlineEdit,
	onStartInlineEdit,
	onToggleCollapsedGroup,
	readonly,
}: {
	activeEditLocks: WorkspaceEditLockMap;
	collapsedGroupIds: string[];
	focusedNodeId: string | null;
	groups: OutlineRowGroup[];
	inlineDraft: JourneyNode | null;
	inlineEditingNodeId: string | null;
	locale: 'zh-TW' | 'en';
	onCancelInlineEdit: () => void;
	onEdit: (node: JourneyNode) => void;
	onInlineDraftChange: (node: JourneyNode) => void;
	onLocate: (nodeId: string, nodeType: NodeType) => void;
	onOpenFullEditor: () => void;
	onRequestDelete: (node: JourneyNode) => void;
	onSaveInlineEdit: () => void;
	onStartInlineEdit: (node: JourneyNode) => void;
	onToggleCollapsedGroup: (groupId: string) => void;
	readonly: boolean;
}) {
	const { t } = useLocale();

	return (
		<div className="outline-groups">
			{groups.map((group) => {
				const collapsed = collapsedGroupIds.includes(group.id);

				return (
					<section className="outline-group" key={group.id}>
						<button
							aria-expanded={!collapsed}
							className="outline-group__header"
							onClick={() => onToggleCollapsedGroup(group.id)}
							type="button"
						>
							<span>{groupLabel(group.id, t)}</span>
							<strong>{group.rows.length}</strong>
						</button>
						{collapsed ? null : (
							<div className="outline-table-shell">
								<table className="outline-table">
									<thead>
										<tr>
											<th>{t('workspace.outline.type')}</th>
											<th>{t('displayName')}</th>
											<th>{t('workspace.outline.connections')}</th>
											<th>{t('updatedAt')}</th>
											<th>{t('workspace.outline.actions')}</th>
										</tr>
									</thead>
									<tbody>
										{group.rows.map((row) => (
											<tr className={row.node.nodeId === focusedNodeId ? 'is-focused' : undefined} key={row.node.nodeId}>
													<td>
														<span className="outline-row__type-pill">{groupLabel(row.node.type, t)}</span>
													</td>
												<td>
													{inlineEditingNodeId === row.node.nodeId ? (
														<InlineEditor draft={inlineDraft ?? row.node} node={row.node} onChange={onInlineDraftChange} />
													) : (
														<div className="outline-row__primary">
															<strong>{row.node.displayName}</strong>
																<span className="outline-row__summary-copy">{row.node.summary || t('noSummaryYet')}</span>
																<small className="outline-row__preview">{row.relatedPreview}</small>
															{getWorkspaceEditLock(activeEditLocks, row.node.nodeId)?.owner === 'node-editor-overlay' ? (
																<small className="outline-row__lock-hint">{t('workspace.outline.lockedByEditor')}</small>
															) : null}
														</div>
													)}
												</td>
													<td>
														<span className="outline-row__metric">{row.connectionCount}</span>
													</td>
													<td>
														<time className="outline-row__timestamp" dateTime={row.node.updatedAt}>
															{formatNodeTimestamp(row.node.updatedAt, locale, { originTimeZone: row.node.updatedAtTimeZone })}
														</time>
													</td>
												<td>
													<div className="hud-toolbar outline-row__actions">
														<button className="hud-chip" onClick={() => onLocate(row.node.nodeId, row.node.type)} type="button">
															{t('workspace.outline.locate')}
														</button>
														{inlineEditingNodeId === row.node.nodeId ? (
															<>
																<button className="hud-chip" disabled={!validateNode(inlineDraft ?? row.node).valid} onClick={onSaveInlineEdit} type="button">
																	{t('workspace.outline.inlineSave')}
																</button>
																<button className="hud-chip" onClick={onOpenFullEditor} type="button">
																	{t('workspace.outline.fullEditor')}
																</button>
																<button className="hud-chip" onClick={onCancelInlineEdit} type="button">
																	{t('workspace.outline.inlineCancel')}
																</button>
															</>
														) : (
															<>
																<button
																	aria-label={formatMessage(t('workspace.outline.inlineEditNode'), { name: row.node.displayName })}
																	className="hud-chip"
																	disabled={readonly || getWorkspaceEditLock(activeEditLocks, row.node.nodeId)?.owner === 'node-editor-overlay'}
																	onClick={() => onStartInlineEdit(row.node)}
																	type="button"
																>
																	{t('workspace.outline.inlineEdit')}
																</button>
																<button className="hud-chip" disabled={readonly || getWorkspaceEditLock(activeEditLocks, row.node.nodeId)?.owner === 'outline-inline'} onClick={() => onEdit(row.node)} type="button">
																	{t('workspace.outline.fullEditor')}
																</button>
															</>
														)}
														<button className="hud-chip" disabled={readonly || getWorkspaceEditLock(activeEditLocks, row.node.nodeId) !== null} onClick={() => onRequestDelete(row.node)} type="button">
															{t('deleteNode')}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</section>
				);
			})}
		</div>
	);
}

function InlineEditor({
	draft,
	node,
	onChange,
}: {
	draft: JourneyNode;
	node: JourneyNode;
	onChange: (node: JourneyNode) => void;
}) {
	const { t } = useLocale();

	return (
		<div className="outline-row__inline-fields">
			<label className="field-stack">
				<span className="field-label">{t('displayName')}</span>
				<input
					aria-label={`${t('displayName')} ${node.displayName}`}
					className="workspace-input"
					onChange={(event) => onChange({ ...draft, displayName: event.target.value })}
					value={draft.displayName}
				/>
			</label>
			<label className="field-stack">
				<span className="field-label">{t('summaryLabel')}</span>
				<textarea
					aria-label={`${t('summaryLabel')} ${node.displayName}`}
					className="workspace-textarea outline-row__summary"
					onChange={(event) => onChange({ ...draft, summary: event.target.value })}
					value={draft.summary}
				/>
			</label>
		</div>
	);
}

function groupLabel(id: 'all' | NodeType, t: ReturnType<typeof useLocale>['t']): string {
	if (id === 'all') {
		return t('workspace.outline.allResults');
	}

	if (id === 'Human') {
		return t('human');
	}

	if (id === 'Project') {
		return t('project');
	}

	return t('occupation');
}

function formatMessage(template: string, values: Record<string, string>): string {
	return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}